/**
 * Scheda giocatore — Trend (V62).
 *
 * Voti, eventi e attributi del giocatore nel tempo, sulle stagioni scelte (la
 * corrente se non se ne sceglie nessuna), in sezioni senza card come nella
 * vista Confronto. Un grafico per sezione: i voti di partite e allenamenti
 * insieme; le linee degli eventi scelti con i minuti, accesi o spenti da un
 * interruttore, in barre su una scala
 * propria; gli attributi scelti dalla tendina, per famiglia o uno a uno.
 * Voti e attributi hanno la scala dal minimo al massimo dei valori mostrati.
 *
 * Tutte le scelte stanno nella query string. Le stagioni si chiedono al
 * server; competizione, eventi e attributi lavorano nel browser. Il Mister
 * riceve solo i voti delle sue squadre-stagione e le misurazioni dello staff
 * delle stesse.
 */

import { memo, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Bar, CartesianGrid, ComposedChart, Line, LineChart, XAxis, YAxis } from 'recharts';
import { GaugeIcon, GoalIcon, StarIcon } from 'lucide-react';
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Field, FieldLabel } from '@/components/ui/field';
import { Switch } from '@/components/ui/switch';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { StatoCaricamento } from '../comuni/Testata.js';
import { dataBreve, useDati } from '../comuni/dati.js';
import { formatoGiorno, giornoUtc } from '../comuni/date.js';
import { Filtro, TUTTI } from '../comuni/FiltriVista.js';
import { SezioneGrafici } from '../comuni/Riquadri.js';
import { LegendaEvidenziabile } from '../comuni/LegendaEvidenziabile.js';
import { NonTrovata } from '../navigazione/NonTrovata.js';
import {
  coloreSerie,
  pallinoSeSolo,
  SCALA_ATTRIBUTI,
  SCALA_CONTEGGI,
  SCALA_VOTO,
  scalaMinMax,
  tacche,
  classeSerie,
} from '../valutazioni/graficiConfronto.js';
import { ICONA_EVENTO } from './iconeEventi.js';
import { SceltaAttributi } from './SceltaAttributi.js';
import {
  EVENTI_TREND,
  type Fonte,
  type RigaTrend,
  type Trend,
  attributiDaQuery,
  attributiMisurati,
  eventiDaQuery,
  righeTrend,
} from './trend.js';
import { Nota } from '../comuni/Nota.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

const formatoAsse = formatoGiorno({ day: '2-digit', month: '2-digit', year: '2-digit' });
const giornoAsse = (data: string) => formatoAsse.format(giornoUtc(data));

/** Una serie del grafico: la chiave dà il colore (`--color-<chiave>`), la fonte i punti. */
interface SerieTrend {
  chiave: string;
  etichetta: string;
  colore: string;
  fonte: Fonte;
}

type Imposta = (valori: Record<string, string | undefined>) => void;

const configDi = (serie: readonly SerieTrend[]): ChartConfig =>
  Object.fromEntries(serie.map((s) => [s.chiave, { label: s.etichetta, color: s.colore }]));

/** Le chiavi come le vuole `scalaMinMax`, che legge solo la chiave. */
const chiaviScala = (serie: readonly SerieTrend[]) =>
  serie.map((s) => ({ chiave: s.chiave, id: s.chiave, nome: s.etichetta }));

export function TrendGiocatore() {
  const { id } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const stagioni = params.get('stagioni');
  // Cambiando stagioni i grafici restano a schermo finché arrivano i nuovi dati.
  const { dati, errore, stato } = useDati<Trend>(
    `/giocatori/${id}/trend${stagioni === null ? '' : `?stagioni=${stagioni}`}`,
    { tieni: true },
  );

  if (stato === 404) return <NonTrovata />;
  if (!dati) return <StatoCaricamento errore={errore} />;

  if (dati.stagioni.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>Nessuna stagione</EmptyTitle>
          <EmptyDescription>
            Il trend si legge per stagione: l'Admin le crea da Stagioni.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  const imposta: Imposta = (valori) => {
    const q = new URLSearchParams(params);
    for (const [chiave, valore] of Object.entries(valori)) {
      if (valore === undefined) q.delete(chiave);
      else q.set(chiave, valore);
    }
    setParams(q, { replace: true });
  };

  const competizione = params.get('competizione') ?? undefined;
  const conVoti = dati.giocatore.votiAllenamento !== undefined;
  const conAttributi = dati.attributi !== undefined;

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <ToggleGroup
          type="multiple"
          variant="outline"
          spacing={2}
          className="flex-wrap"
          aria-label="Stagioni"
          value={dati.scelte}
          onValueChange={(scelte) =>
            imposta({
              // Nell'ordine delle stagioni, così lo stesso insieme è lo stesso indirizzo.
              stagioni: dati.stagioni
                .map((s) => s.id)
                .filter((s) => scelte.includes(s))
                .join(','),
            })
          }
        >
          {dati.stagioni.map((s) => (
            <ToggleGroupItem key={s.id} value={s.id}>
              {s.nome}
              {s.corrente && <span className="text-muted-foreground">· corrente</span>}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <Filtro
          etichetta="Competizione"
          valore={competizione ?? TUTTI}
          onCambia={(v) => imposta({ competizione: v === TUTTI ? undefined : v })}
          opzioni={[
            { valore: TUTTI, etichetta: 'Tutte' },
            ...dati.competizioni.map((c) => ({ valore: c.id, etichetta: c.nome })),
          ]}
        />
      </div>

      {dati.scelte.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Scegli almeno una stagione</EmptyTitle>
            <EmptyDescription>
              I grafici mostrano le stagioni scelte, una dopo l'altra sullo stesso asse.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <>
          {conVoti && <Voti dati={dati} competizione={competizione} />}
          <Eventi dati={dati} competizione={competizione} params={params} imposta={imposta} />
          {conAttributi && <Attributi dati={dati} params={params} imposta={imposta} />}
        </>
      )}
    </div>
  );
}

const SERIE_VOTI: SerieTrend[] = [
  { chiave: 'partite', etichetta: 'Partite', colore: coloreSerie(0), fonte: { tipo: 'voto' } },
  {
    chiave: 'allenamenti',
    etichetta: 'Allenamenti',
    colore: coloreSerie(1),
    fonte: { tipo: 'votoAllenamento' },
  },
];
const CONFIG_VOTI = configDi(SERIE_VOTI);

function Voti({ dati, competizione }: { dati: Trend; competizione: string | undefined }) {
  const righe = useMemo(
    () => righeTrend(SERIE_VOTI, dati.giocatore, { competizione }),
    [dati, competizione],
  );
  const ticks = useMemo(
    () => tacche(scalaMinMax(righe, chiaviScala(SERIE_VOTI), SCALA_VOTO)),
    [righe],
  );
  return (
    <SezioneGrafici titolo="Voti" Icona={StarIcon}>
      {righe.length === 0 ? (
        <Vuoto competizione={competizione} />
      ) : (
        <GraficoLinee righe={righe} serie={SERIE_VOTI} config={CONFIG_VOTI} ticks={ticks} />
      )}
      <Nota>
        Un punto per partita conclusa o allenamento votato; due voti dello stesso giorno fanno la
        media. Gli allenamenti non hanno competizione: con un filtro non compaiono. La scala va dal
        voto più basso al più alto.
      </Nota>
    </SezioneGrafici>
  );
}

const SERIE_MINUTI: SerieTrend = {
  chiave: 'minuti',
  etichetta: 'Minuti',
  colore: 'var(--muted-foreground)',
  fonte: { tipo: 'evento', evento: 'minuti' },
};

function Eventi({
  dati,
  competizione,
  params,
  imposta,
}: {
  dati: Trend;
  competizione: string | undefined;
  params: URLSearchParams;
  imposta: Imposta;
}) {
  const progressivo = params.get('progressivo') === '1';
  const valoreEventi = params.get('eventi');
  const scelti = useMemo(() => eventiDaQuery(valoreEventi), [valoreEventi]);
  // I minuti si spengono, e restano spenti nella query string; accesi di partenza.
  const conMinuti = params.get('minuti') !== '0';
  // Il colore segue l'ordine degli eventi scelti, come i pulsanti.
  const serie = useMemo(
    () =>
      EVENTI_TREND.filter((e) => scelti.includes(e.chiave)).map((e, i): SerieTrend => ({
        chiave: e.chiave,
        etichetta: e.etichetta,
        colore: coloreSerie(i),
        fonte: { tipo: 'evento', evento: e.chiave },
      })),
    [scelti],
  );
  const righe = useMemo(
    () =>
      righeTrend(conMinuti ? [...serie, SERIE_MINUTI] : serie, dati.giocatore, {
        competizione,
        progressivo,
      }),
    [serie, conMinuti, dati, competizione, progressivo],
  );

  return (
    <SezioneGrafici
      titolo="Eventi"
      Icona={GoalIcon}
      azione={
        <>
          <Field orientation="horizontal" className="w-auto">
            <Switch
              id="trend-minuti"
              checked={conMinuti}
              onCheckedChange={(on) => imposta({ minuti: on ? undefined : '0' })}
            />
            <FieldLabel htmlFor="trend-minuti">Minuti</FieldLabel>
          </Field>
          <Field orientation="horizontal" className="w-auto">
            <Switch
              id="trend-progressivo"
              checked={progressivo}
              onCheckedChange={(on) => imposta({ progressivo: on ? '1' : undefined })}
            />
            <FieldLabel htmlFor="trend-progressivo">Progressivo</FieldLabel>
          </Field>
        </>
      }
    >
      <ToggleGroup
        type="multiple"
        variant="outline"
        spacing={2}
        className="flex-wrap"
        aria-label="Eventi"
        value={scelti}
        onValueChange={(e) => imposta({ eventi: e.join(',') })}
      >
        {EVENTI_TREND.map((e) => (
          <ToggleGroupItem key={e.chiave} value={e.chiave}>
            {ICONA_EVENTO[e.chiave]}
            {e.etichetta}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      {serie.length === 0 && !conMinuti ? (
        <StatoVuoto titolo="Scegli almeno un evento o i minuti" />
      ) : righe.length === 0 ? (
        <Vuoto competizione={competizione} />
      ) : (
        <GraficoEventi righe={righe} serie={serie} conMinuti={conMinuti} />
      )}
      <Nota>
        Un punto per partita conclusa; due partite lo stesso giorno sommano gli eventi. I minuti, se
        accesi, sono le barre, con la loro scala a destra. Con il progressivo eventi e minuti si
        sommano partita dopo partita. I tiri sono la somma di quelli in porta e fuori.
      </Nota>
    </SezioneGrafici>
  );
}

function Attributi({
  dati,
  params,
  imposta,
}: {
  dati: Trend;
  params: URLSearchParams;
  imposta: Imposta;
}) {
  const misurati = useMemo(() => attributiMisurati(dati), [dati]);
  const valore = params.get('attributi');
  const scelti = useMemo(() => attributiDaQuery(valore, misurati), [valore, misurati]);
  const serie = useMemo(
    () =>
      scelti.flatMap((aid, i): SerieTrend[] => {
        const a = misurati.find((x) => x.id === aid);
        return a
          ? [
              {
                chiave: `a${i}`,
                etichetta: a.nome,
                colore: coloreSerie(i),
                fonte: { tipo: 'attributo', id: a.id },
              },
            ]
          : [];
      }),
    [scelti, misurati],
  );
  const config = useMemo(() => configDi(serie), [serie]);
  const righe = useMemo(() => righeTrend(serie, dati.giocatore), [serie, dati]);
  const ticks = useMemo(
    () => tacche(scalaMinMax(righe, chiaviScala(serie), SCALA_ATTRIBUTI)),
    [righe, serie],
  );

  // Chi c'era resta al suo posto, e quindi al suo colore; i nuovi in coda.
  const scegli = (nuovi: string[]) => {
    const prossimi = [
      ...scelti.filter((x) => nuovi.includes(x)),
      ...nuovi.filter((x) => !scelti.includes(x)),
    ];
    imposta({ attributi: prossimi.join(',') });
  };

  if (misurati.length === 0) {
    return (
      <SezioneGrafici titolo="Attributi" Icona={GaugeIcon}>
        <StatoVuoto titolo="Nessuna misurazione nelle stagioni scelte" />
      </SezioneGrafici>
    );
  }

  return (
    <SezioneGrafici
      titolo="Attributi"
      Icona={GaugeIcon}
      azione={
        <SceltaAttributi
          id="trend-attributi"
          attributi={misurati}
          scelti={scelti}
          colore={(aid) => {
            const i = scelti.indexOf(aid);
            return i < 0 ? undefined : coloreSerie(i);
          }}
          onCambia={scegli}
        />
      }
    >
      {serie.length === 0 ? (
        <StatoVuoto titolo="Scegli almeno un attributo" />
      ) : (
        <GraficoLinee righe={righe} serie={serie} config={config} ticks={ticks} />
      )}
      <Nota>
        Nella tendina gli attributi misurati nelle stagioni scelte; una famiglia li sceglie tutti.
        Un punto per giorno di misurazione, due misurazioni dello stesso giorno fanno la media. La
        scala va dal valore più basso al più alto dei valori mostrati, non da 1 a 100.
      </Nota>
    </SezioneGrafici>
  );
}

function Vuoto({ competizione }: { competizione: string | undefined }) {
  return (
    <StatoVuoto
      titolo={
        competizione
          ? 'Nessun dato nelle stagioni scelte per questa competizione'
          : 'Nessun dato nelle stagioni scelte'
      }
    />
  );
}

/** L'asse delle date: categorie equidistanti, fra due stagioni non resta il vuoto dell'estate. */
const asseDate = (
  <XAxis
    dataKey="data"
    padding={{ left: 16, right: 16 }}
    tickFormatter={giornoAsse}
    tickLine={false}
    axisLine={false}
    tickMargin={8}
    minTickGap={24}
  />
);

const tooltip = (
  <ChartTooltip
    content={
      <ChartTooltipContent
        indicator="dot"
        labelFormatter={(data) => (typeof data === 'string' ? dataBreve(data) : null)}
      />
    }
  />
);

const legenda = <ChartLegend wrapperStyle={{ paddingTop: 8 }} content={<LegendaEvidenziabile />} />;

const linea = (righe: RigaTrend[], s: SerieTrend, yAxisId?: string) => (
  <Line
    key={s.chiave}
    yAxisId={yAxisId}
    dataKey={s.chiave}
    className={classeSerie(s.chiave)}
    type="monotone"
    stroke={`var(--color-${s.chiave})`}
    strokeWidth={3}
    dot={pallinoSeSolo(righe, s.chiave)}
    connectNulls
  />
);

// Recharts rianima a ogni render anche a dati invariati: i grafici sono memo,
// con props stabili, così cambiare una scelta non ridisegna gli altri.
const GraficoLinee = memo(function GraficoLinee({
  righe,
  serie,
  config,
  ticks,
}: {
  righe: RigaTrend[];
  serie: SerieTrend[];
  config: ChartConfig;
  ticks: number[];
}) {
  return (
    <ChartContainer config={config} className="aspect-auto h-72 w-full">
      <LineChart data={righe} margin={{ top: 8, right: 16 }}>
        <CartesianGrid vertical={false} />
        {asseDate}
        <YAxis
          domain={[ticks[0]!, ticks.at(-1)!]}
          ticks={ticks}
          tickLine={false}
          axisLine={false}
          width={32}
        />
        {tooltip}
        {legenda}
        {serie.map((s) => linea(righe, s))}
      </LineChart>
    </ChartContainer>
  );
});

/** Le linee degli eventi sulla scala di sinistra, le barre dei minuti sulla loro a destra. */
const GraficoEventi = memo(function GraficoEventi({
  righe,
  serie,
  conMinuti,
}: {
  righe: RigaTrend[];
  serie: SerieTrend[];
  conMinuti: boolean;
}) {
  const config = useMemo(
    () => configDi(conMinuti ? [...serie, SERIE_MINUTI] : serie),
    [serie, conMinuti],
  );
  const ticksEventi = useMemo(
    () => tacche(scalaMinMax(righe, chiaviScala(serie), SCALA_CONTEGGI)),
    [righe, serie],
  );
  // Le barre partono da zero: una barra tagliata direbbe minuti che non ci sono.
  const ticksMinuti = useMemo(
    () => tacche([0, Math.max(10, ...righe.map((r) => Number(r.minuti ?? 0)))]),
    [righe],
  );
  return (
    <ChartContainer config={config} className="aspect-auto h-72 w-full">
      <ComposedChart data={righe} margin={{ top: 8 }}>
        <CartesianGrid vertical={false} />
        {asseDate}
        <YAxis
          yAxisId="eventi"
          hide={serie.length === 0}
          domain={[ticksEventi[0]!, ticksEventi.at(-1)!]}
          ticks={ticksEventi}
          allowDecimals={false}
          tickLine={false}
          axisLine={false}
          width={32}
        />
        <YAxis
          yAxisId="minuti"
          orientation="right"
          hide={!conMinuti}
          domain={[ticksMinuti[0]!, ticksMinuti.at(-1)!]}
          ticks={ticksMinuti}
          allowDecimals={false}
          tickLine={false}
          axisLine={false}
          width={40}
        />
        {tooltip}
        {legenda}
        {conMinuti && (
          <Bar
            yAxisId="minuti"
            dataKey="minuti"
            className={classeSerie('minuti')}
            fill="var(--color-minuti)"
            fillOpacity={0.25}
            radius={[4, 4, 0, 0]}
            maxBarSize={24}
          />
        )}
        {serie.map((s) => linea(righe, s, 'eventi'))}
      </ComposedChart>
    </ChartContainer>
  );
});
